import React from "react";
import { useDispatch, useSelector } from  "react-redux";
import { Icon } from "rhombus-react";
import classNames from "classnames";
import { BasicAction, LinkAction } from "widgets/ActionMenu";
import { hasPermission, noop }  from "utils/common";
import { viewDTreeModal, assignDTree, unassignDTree } from "reducers/DTrees";
import { loadCreative } from "reducers/CreativeDetails";
import { loadCreativeVersions } from "reducers/CreativeVersions";
import { loadCreatives } from "reducers/Creatives";
import { tellSentry } from "utils/Rave";
import { D_TREE_URL } from "utils/constants";

const DT = "Decision Tree";

export function BasicDTreeLinkAction({ icon, name }, rowInfo){
	if(!name){
		tellSentry(`Missing or invalid link for row item: ${name}`);
	}
	return (
		<LinkAction
			key={`d-tree-${name}`}
			iconName={icon}
			useMenuItem
			className="action-link action-item"
			iconClass="bounce-circle"
			path={`/library/${rowInfo.libraryId}/creative/${rowInfo.creativeId}/versions/modal/dtrees/${name}/`} />
	);
}

export function BasicDTreeMenuAction({ icon, name }, rowInfo = {}){
	const dispatch = useDispatch();

	let handleClick =()=>{
		dispatch(viewDTreeModal(name, rowInfo));
	};

	if(rowInfo.actions){
		let { openViewVersionsModal, openLinkedCreativesModal } = rowInfo.actions;
		if(name === VIEW_VERSIONS_IN_USE.name){
			handleClick =()=>{
				openViewVersionsModal({ name, ...rowInfo });
			};
		}
		if(name===VIEW_LINKED_CREATIVES.name){
			handleClick =()=>{
				openLinkedCreativesModal({ name, ...rowInfo });
			};
		}
	}

	return (
		<BasicAction
			iconName={icon}
			name={name}
			handleClick={handleClick}
		/>
	);
}

export const ASSIGN =  {
	permission:"Assign Decision Tree",
	shouldDisplay:({ user })=>{return hasPermission(user, DT, ASSIGN.permission);},
	component({ dependentValues }){
		let { libraryId, creativeId, assigned, assignable, id, actions } = dependentValues
			, creativeIds = [].concat(creativeId)
			, { closeModal, defaultVersion } = actions
			, assignedVersionId = defaultVersion?.id
			, dispatch = useDispatch()
			, {
				bulkCreativeIds,
				creativeVersions,
			} = useSelector((state) => {
				return {
					bulkCreativeIds: state.modal?.modalOptions?.creativeIds,
					creativeVersions: state.creativeVersions.items,
				};
			})
			, isBulkAssignment = bulkCreativeIds?.length
			, canAssign = (!assigned || isBulkAssignment) && assignable
			, icon = (assigned && !isBulkAssignment) ? "far fa-minus-circle" : "far fa-plus-circle"
			, name = (assigned && !isBulkAssignment) ? "Unassign" : "Assign"
			, handleClick = ()=>{
				if(canAssign){
					 dispatch(assignDTree(libraryId, creativeIds, id))
						.then((resp)=>{
							if(resp && resp.success){
								// only dispatch loadCreative and loadCreativeVersions if not doing bulk assignments
								if(!bulkCreativeIds){
									dispatch(loadCreative(creativeIds));
									dispatch(loadCreativeVersions(libraryId, creativeIds));
								}
								closeModal();

								if(!creativeVersions.length) {
									//load creatives again if not on versions grid
									dispatch(loadCreatives(libraryId));
								}
							}
						});
				} else if(assigned){
					dispatch(unassignDTree(libraryId, creativeId, assignedVersionId))
						.then((resp)=>{
							if(resp && resp.success){
								dispatch(loadCreative(creativeId));
								dispatch(loadCreativeVersions(libraryId, creativeId));
								closeModal();
								//bulk unassign not possible ATM as there is no assignedVersionId available
								if(!bulkCreativeIds) {
									dispatch(loadCreatives(libraryId));
								 }
							}
						});
				}
			};

		function renderIcon(){
			return (
				<Icon
					name={icon}
					className={classNames("bounce-circle", { "is-disabled":!assignable })}
					data-tip={name}
					onClick={handleClick}
				/>
			);
		}

		return (
			<div key="assign">
				<span className="action-icon">
					{renderIcon()}
				</span>
			</div>
		);
	},
};


export const EDIT =  {
	icon:"edit",
	name:"Edit D-tree",
	className:"edit-dtree",
	permission:"Edit Decision Tree",
	pathFn:dependentValues=>`${D_TREE_URL}/v/edit/${dependentValues.id}?creative=${dependentValues.creativeId}`,
	shouldDisplay:({ user })=> hasPermission(user, DT, EDIT.permission),
	component:({ dependentValues })=>{
		let { icon, name, className, pathFn } = EDIT
			, path = pathFn(dependentValues)
			, handleClick = ()=> window.open(path, "_BLANK")
		;
		return (
			<div key={name}>
				<span className={`action-icon ${className}`}>
					<Icon
						name={icon}
						className="bounce-circle"
						data-tip={name}
						onClick={handleClick}
					/>
				</span>
			</div>
		);
	}
	,
};

export const PREVIEW_TREE =  {
	icon:"preview",
	name:"Preview Tree",
	className:"preview-dtree",
	pathFn:dependentValues=>`${D_TREE_URL}/v/view/${dependentValues.id}/`,
	shouldDisplay:({ dependentValues })=>{return true;},
	component:({ dependentValues })=>{
		let { icon, name, className, pathFn } = PREVIEW_TREE
			, path = pathFn(dependentValues)
			, handleClick = ()=> window.open(path, "_BLANK")
		;
		return (
			<div key={name}>
				<span className={`action-icon ${className}`}>
					<Icon
						name={icon}
						className="bounce-circle"
						data-tip={name}
						onClick={handleClick}
					/>
				</span>
			</div>
		);
	},
};

export const SHOW_MISSING =  {
	icon:"search",
	name:"View Missing Versions",
	className:"show-missing-dtree",
	shouldDisplay:({ dependentValues })=>{
		return dependentValues?.versionsMissingFromCreatives?.length || false;
	},
	component:({ dependentValues })=>{
		let { openViewVersionsModal } = dependentValues.actions || {}
			, { icon, name, className } = SHOW_MISSING
			, handleClick =()=>openViewVersionsModal({ modalName:name, dependentValues })
		;
		return (
			<div key={name}>
				<span className={`action-icon ${className}`}>
					<Icon
						name={icon}
						className="bounce-circle"
						data-tip={name}
						onClick={handleClick}
					/>
				</span>
			</div>
		);
	},
};

const DUPLICATE = {
	icon:"copy",
	name:"Duplicate DTree",
	className:"duplicate-dtree",
	permission:"Create Decision Tree",
	shouldDisplay:({ user })=>{return hasPermission(user, DT, DUPLICATE.permission);},
	component:({ dependentValues })=> {
		let { actions = {}, id = "", name:dtreeName  } = dependentValues
			, { duplicateDtreeModal = noop } = actions
			, { icon, name, className } = DUPLICATE
		;
		return (
			<BasicAction
				iconName={icon}
				name={name}
				className={className}
				handleClick = {() => {
					duplicateDtreeModal({ modalAction:name, dtreeId:id, name:dtreeName });
				}}
			/>
		);
	},
};

const VIEW_VERSIONS_IN_USE = {
	icon:"list",
	name:"View Versions in Use",
	className:"view-versions-in-use",
	component:({ dependentValues })=>	{
		let { openViewVersionsModal } = dependentValues.actions || {}
			, { icon, name, className } = VIEW_VERSIONS_IN_USE
			;

		return (
			<BasicAction
				iconName={icon}
				name={name}
				className={className}
				handleClick = {() => { openViewVersionsModal({ modalName:name, dependentValues }); }}
			/>
		);
	},
};

const VIEW_LINKED_CREATIVES = {
	shouldDisplay: ({ dependentValues }) => {
		let { linkedCreatives = []} = dependentValues || {};
		return linkedCreatives.length;
	},
	component({ dependentValues: { actions, ...dtree } }){
		let { openLinkedCreativesModal } = actions || {};
		return (
			<BasicAction
				iconName="creatives"
				name="View Linked Creatives"
				className="view-linked-creatives"
				handleClick = {() => { openLinkedCreativesModal(dtree); }}
			/>
		);
	},
};

export const MORE_ACTIONS = [
	[DUPLICATE,
		VIEW_VERSIONS_IN_USE,
		VIEW_LINKED_CREATIVES]
];

export function dTreeActionList(list, actionContext={}){
	let displayableActions = list.filter(({ shouldDisplay }) => {
		return shouldDisplay(actionContext);
	});

	return displayableActions.map(({ component = noop })=>component(actionContext));
}

const DTREE_ACTIONS = [
	ASSIGN,
	EDIT,
	PREVIEW_TREE,
	SHOW_MISSING,
];

export default DTREE_ACTIONS;
